import { ReactNode, useState } from "react";

import { t } from "i18next";

export interface ModalOptions {
  content: ReactNode | string;
  title?: string;
  confirmButtonText?: ReactNode | string;
  confirmButtonLoading?: boolean;
  confirmButtonVariant?: "primary" | "secondary" | "success" | "danger" | "warning" | "link";
  cancelButtonText?: ReactNode | string;
  size?: "sm" | "md" | "lg";
  enableCloseButton?: boolean;
  enableCloseOnBackdrop?: boolean;
  handleCancel?: () => void;
  handleConfirm?: () => void;
  handleHide?: () => void;
}

interface UseModalReturnTypes {
  showModal: boolean;
  handleModal: (content?: ModalOptions) => void;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  modalOptions: ModalOptions
}

export default (): UseModalReturnTypes => {
  const [showModal, setShowModal] = useState(false);
  const [modalOptions, setModalOptions] = useState<ModalOptions>({
    content: "Modal Content",
    title: "Modal Title",
    confirmButtonText: t("core.actions.submit"),
    confirmButtonLoading: false,
    confirmButtonVariant: "primary",
    cancelButtonText: t("core.actions.cancel"),
    size: "md",
    enableCloseButton: true,
    enableCloseOnBackdrop: true,
    handleCancel: () => {},
    handleConfirm: () => {},
    handleHide: () => {},
  });

  // Calling with options opens the modal (and sets its content); calling with no argument
  // closes it. Using explicit booleans (instead of toggling !showModal) avoids a stale-closure
  // desync where opening a modal while one was already shown could immediately close it,
  // which left confirmation dialogs (e.g. delete schedule) without their footer/buttons.
  const handleModal = (content?: ModalOptions) => {
    if (content != null) {
      setModalOptions(content);
      setShowModal(true);
    } else {
      setShowModal(false);
    }
  };

  return { showModal, setShowModal, handleModal, modalOptions };
};
